import type { Probe, Finding, SignalValue, ProbeContext } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { glReportedOs } from '../engine/normalize';

const COHERENCE_SOURCE = 'https://w3c.github.io/webappsec-privacy-considerations/';
const HEADLESS_SOURCE = 'https://developer.chrome.org/blog/headless/';

type ClaimedOs = 'Windows' | 'macOS' | 'Linux' | 'Android' | 'iOS' | 'ChromeOS' | 'unknown';

interface Claims {
  ua: string;
  os: ClaimedOs;
  isMobile: boolean;
}

/** Reads the values the coherence checks need from the already-collected signals. */
function readSignals(signals: Record<string, Record<string, SignalValue>>): {
  claims: Claims;
  platform: string | null;
  glRenderer: string | null;
  glVendor: string | null;
  chPlatform: string | null;
  dpr: number | null;
  screenW: number | null;
  screenH: number | null;
} {
  const general = signals['general'] ?? {};
  const webgl = signals['webgl'] ?? {};
  const ch = signals['clientHints'] ?? {};
  const display = signals['display'] ?? {};

  const ua = (general.userAgent?.value as string) ?? '';
  const os: ClaimedOs = /Windows/i.test(ua)
    ? 'Windows'
    : /Android/i.test(ua)
      ? 'Android'
      : /(iPhone|iPad|iPod)/i.test(ua)
        ? 'iOS'
        : /Macintosh|Mac OS X/i.test(ua)
          ? 'macOS'
          : /CrOS/i.test(ua)
            ? 'ChromeOS'
            : /Linux/i.test(ua)
              ? 'Linux'
              : 'unknown';

  return {
    claims: { ua, os, isMobile: /Mobile|Android|iPhone|iPad/i.test(ua) },
    platform: (general.platform?.value as string) ?? null,
    glRenderer: (webgl.unmaskedRenderer?.value as string) ?? (webgl.renderer?.value as string) ?? null,
    glVendor: (webgl.unmaskedVendor?.value as string) ?? (webgl.vendor?.value as string) ?? null,
    chPlatform: (ch.platform?.value as string) ?? null,
    dpr: (display.devicePixelRatio?.value as number) ?? null,
    screenW: (display.width?.value as number) ?? null,
    screenH: (display.height?.value as number) ?? null,
  };
}

/**
 * Coherence checks — "claimed identity vs observed properties".
 *
 * The central idea (and the reference site's stated thesis): a single odd
 * property is usually harmless, but properties that would normally agree and
 * don't are strong evidence. We check the highest-value pairs only, each with
 * an explicit confidence, so speculative ones never inflate the score.
 */
export const coherenceProbe: Probe = {
  id: 'coherence',
  label: 'Identity coherence',
  layer: 'browser',
  tier: 'client',
  category: 'browser',
  description: 'Cross-checks the claimed browser/OS identity against observed properties',
  // Must run after the probes whose signals it reasons about.
  after: ['general', 'webgl', 'clientHints', 'display'],
  budgetMs: 500,

  async run(ctx) {
    const signals = readAllSignals(ctx);
    const { claims, platform, glRenderer, glVendor, chPlatform, dpr, screenW } = readSignals(
      signals,
    );

    const findings: Finding[] = [];

    // 1) UA says one OS, navigator.platform says another.
    const platformOs: ClaimedOs =
      platform === 'Win32'
        ? 'Windows'
        : platform === 'MacIntel'
          ? 'macOS'
          : platform === 'Linux x86_64' || platform === 'Linux aarch64'
            ? 'Linux'
            : 'unknown';
    if (claims.os !== 'unknown' && platformOs !== 'unknown' && claims.os !== platformOs) {
      findings.push({
        id: 'coherence.ua-platform-mismatch',
        severity: 'medium',
        category: 'browser',
        layer: 'browser',
        confidence: 'high',
        title: 'User-Agent OS disagrees with navigator.platform',
        detail:
          'The User-Agent string and navigator.platform describe different operating systems. Genuine browsers keep these consistent; a mismatch usually means one was patched.',
        sources: [COHERENCE_SOURCE],
        evidence: [`UA os=${claims.os}`, `navigator.platform=${platform}`],
        scoreContribution: 25,
      });
    }

    // 2) UA says one OS, the GPU stack implies another (strongest coherence check).
    if (claims.os !== 'unknown' && glRenderer) {
      const implied = glReportedOs(glRenderer, glVendor ?? '');
      const conflict =
        (claims.os === 'Windows' && implied === 'macOS') ||
        (claims.os === 'macOS' && implied === 'Windows') ||
        ((claims.os === 'Windows' || claims.os === 'macOS') && implied === 'mobile');
      if (conflict) {
        findings.push({
          id: 'coherence.ua-gl-mismatch',
          severity: 'high',
          category: 'hardware',
          layer: 'gpu',
          confidence: 'high',
          title: 'User-Agent OS disagrees with the WebGL graphics stack',
          detail:
            'The claimed operating system and the GPU vendor that actually rendered the page are from different platforms. Neither property is suspicious alone; the disagreement is the signal. This is a common artifact of UA spoofing.',
          sources: [COHERENCE_SOURCE, HEADLESS_SOURCE],
          evidence: [`UA os=${claims.os}`, `WebGL implies=${implied}`, `renderer=${glRenderer}`],
          scoreContribution: 30,
        });
      }
    }

    // 3) Client-Hints platform vs UA (also handled in clientHints; this is the
    //    cross-cutting view so it is visible even if that probe was skipped).
    if (claims.os !== 'unknown' && chPlatform) {
      const conflict =
        (claims.os === 'Windows' && chPlatform === 'macOS') ||
        (claims.os === 'macOS' && chPlatform === 'Windows');
      if (conflict) {
        findings.push({
          id: 'coherence.ua-ch-mismatch',
          severity: 'medium',
          category: 'browser',
          layer: 'browser',
          confidence: 'medium',
          title: 'User-Agent OS disagrees with Sec-CH-UA-Platform',
          detail:
            'The User-Agent string and the User-Agent Client Hints platform describe different operating systems.',
          sources: [COHERENCE_SOURCE],
          evidence: [`UA os=${claims.os}`, `Sec-CH-UA-Platform=${chPlatform}`],
          scoreContribution: 20,
        });
      }
    }

    // 4) Mobile UA with no touch support, or desktop UA claiming touch.
    const maxTouchPoints = (signals['general']?.maxTouchPoints?.value as number) ?? 0;
    if (claims.isMobile && maxTouchPoints === 0 && claims.os === 'Android') {
      findings.push({
        id: 'coherence.mobile-without-touch',
        severity: 'medium',
        category: 'browser',
        layer: 'browser',
        confidence: 'medium',
        title: 'Mobile User-Agent reports zero touch points',
        detail:
          'An Android User-Agent with navigator.maxTouchPoints === 0 is unusual; real Android devices report at least one touch point.',
        sources: [COHERENCE_SOURCE],
        evidence: [`UA os=Android`, `maxTouchPoints=${maxTouchPoints}`],
        scoreContribution: 15,
      });
    }

    // 5) devicePixelRatio vs screen size — a Retina Mac should have DPR>=2 on
    //    built-in displays; a 1 on a 1440-wide "Mac" screen is a soft tell.
    if (claims.os === 'macOS' && dpr === 1 && screenW && screenW >= 1600) {
      findings.push({
        id: 'coherence.mac-dpr1',
        severity: 'low',
        category: 'hardware',
        layer: 'display',
        confidence: 'low',
        title: 'macOS User-Agent with devicePixelRatio 1 on a large screen',
        detail:
          'Built-in macOS Retina displays report a devicePixelRatio of 2 or greater. A value of 1 on a large screen is unusual, though external non-Retina displays can produce it.',
        sources: [COHERENCE_SOURCE],
        evidence: [`UA os=macOS`, `devicePixelRatio=${dpr}`, `screen.width=${screenW}`],
        scoreContribution: 0,
      });
    }

    // 6) availHeight exceeding screen height is impossible; guard the display
    //    probe's finding by recomputing here so it always appears.
    const availH = (signals['display']?.availHeight?.value as number) ?? null;
    const h = (signals['display']?.height?.value as number) ?? null;
    if (availH !== null && h !== null && availH > h) {
      findings.push({
        id: 'coherence.avail-height-impossible',
        severity: 'medium',
        category: 'hardware',
        layer: 'display',
        confidence: 'high',
        title: 'screen.availHeight exceeds screen.height',
        detail:
          'The available screen height is larger than the total screen height, which is not possible on a real display. This indicates a spoofed or synthetic screen object.',
        sources: [COHERENCE_SOURCE],
        evidence: [`availHeight=${availH}`, `height=${h}`],
        scoreContribution: 20,
      });
    }

    return {
      signals: {
        claimedOs: ok(claims.os, 'derived'),
        platformOs: ok(platformOs, 'derived'),
        glImpliedOs: ok(glRenderer ? glReportedOs(glRenderer, glVendor ?? '') : 'unknown', 'derived'),
        uaPlatformAgrees: ok(
          claims.os === 'unknown' || platformOs === 'unknown' ? null : claims.os === platformOs,
          'derived',
        ),
        uaGlAgrees: ok(
          claims.os === 'unknown' || !glRenderer
            ? null
            : !(
                (claims.os === 'Windows' && glReportedOs(glRenderer, glVendor ?? '') === 'macOS') ||
                (claims.os === 'macOS' && glReportedOs(glRenderer, glVendor ?? '') === 'Windows')
              ),
          'derived',
        ),
      },
      findings,
    };
  },
};

/**
 * Rebuild a {probeId -> {signalName -> SignalValue}} view from the shared
 * context. The runner exposes sibling values through readSignal(); we only need
 * a handful of named signals, so we pull those directly rather than
 * materializing every value.
 */
function readAllSignals(ctx: ProbeContext): Record<string, Record<string, SignalValue>> {
  const wanted: Record<string, string[]> = {
    general: ['userAgent', 'platform', 'maxTouchPoints'],
    webgl: ['unmaskedRenderer', 'renderer', 'unmaskedVendor', 'vendor'],
    clientHints: ['platform'],
    display: ['devicePixelRatio', 'width', 'height', 'availHeight'],
  };
  const out: Record<string, Record<string, SignalValue>> = {};
  for (const [probeId, names] of Object.entries(wanted)) {
    const bucket: Record<string, SignalValue> = {};
    for (const name of names) {
      // readSignal returns the raw value; we only need the 'ok' vs null shape.
      const value = ctx.readSignal(probeId, name);
      bucket[name] = { value: value ?? null, status: value == null ? 'unavailable' : 'ok', origin: 'client', durationMs: 0 };
    }
    out[probeId] = bucket;
  }
  return out;
}

registerProbe(coherenceProbe);
